import { EMPTY_VALUE, priceDecimals } from '@outpoll/utils';
import type { NumberFormatOptions, useFormatter } from 'next-intl';
import type { FuturesPair, PairTag } from '@/lib/api/futures/client';

type Formatter = ReturnType<typeof useFormatter>;

export type ChangeDirection = 'up' | 'down' | 'flat';

/** A futures kind as its terminal path names it. */
export type FuturesKind = 'usdt' | 'usdc' | 'inverse';

/** A header mega menu's pair list: one futures kind, every TradFi pair, or one admin tag's pairs. */
export type PairMarket = FuturesKind | 'tradfi' | { tag: string };

export const CHANGE_DIRECTION_TEXT: Record<ChangeDirection, string> = {
  up: 'text-v2-green',
  down: 'text-v2-btn-premium-red-light',
  flat: 'text-v2-text-secondary',
};

const CHANGE_PERCENT_FORMAT: NumberFormatOptions = {
  style: 'percent',
  signDisplay: 'exceptZero',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/** `Intl` options that pin a price to the pair's tick precision. */
export function priceFormatOptions(tickSize: number): NumberFormatOptions {
  const digits = priceDecimals(tickSize);
  return { minimumFractionDigits: digits, maximumFractionDigits: digits };
}

export function changeDirection(changePct: number | undefined): ChangeDirection {
  if (!changePct) return 'flat';
  return changePct > 0 ? 'up' : 'down';
}

/** Symbol or base currency contains the query, case-insensitive. */
export function matchesPair(pair: FuturesPair, query: string): boolean {
  const q = query.toUpperCase();
  return pair.symbol.toUpperCase().includes(q) || pair.baseCurrency.toUpperCase().includes(q);
}

/** 🔥 trending pairs first, then server order. */
export function pickTopPairs(pairs: FuturesPair[], limit: number): FuturesPair[] {
  const trending = pairs.filter((p) => p.trending);
  if (trending.length >= limit) return trending.slice(0, limit);
  return [...trending, ...pairs.filter((p) => !p.trending)].slice(0, limit);
}

/** Last price at the pair's tick precision, or a dash before the ticker arrives. */
export function formatPairPrice(format: Formatter, pair: FuturesPair): string {
  if (pair.last == null) return EMPTY_VALUE;
  return format.number(pair.last, priceFormatOptions(pair.tickSize));
}

/** The day's change as a signed percentage, or a dash before the ticker arrives. */
export function formatPairChange(format: Formatter, pair: FuturesPair): string {
  if (pair.changePct == null) return EMPTY_VALUE;
  return format.number(pair.changePct / 100, CHANGE_PERCENT_FORMAT);
}

function inMarket(pair: FuturesPair, market: PairMarket): boolean {
  if (typeof market === 'object') return pair.tags.includes(market.tag);
  if (market === 'tradfi') return pair.isTradFi;
  return pair.path.startsWith(`/trade/${market}/`);
}

/** One market's pairs (a futures kind by terminal path, TradFi, or a tag) matching the query, 🔥 first. */
export function filterMarketPairs(
  pairs: FuturesPair[],
  market: PairMarket,
  query: string,
): FuturesPair[] {
  const matching = pairs.filter(
    (pair) => inMarket(pair, market) && (!query || matchesPair(pair, query)),
  );
  return pickTopPairs(matching, matching.length);
}

/** A TradFi menu row: one tag's pairs, leading to the list's first pair. */
export interface TradFiRow extends PairTag {
  market: PairMarket;
  firstPairPath: string;
}

// shortcut: one all-TradFi row while an env's feed has no tags yet; drop once every env sends them.
const ALL_TRADFI_ROW: Omit<TradFiRow, 'firstPairPath'> = {
  code: 'STOCK',
  name: 'Stocks',
  market: 'tradfi',
};

/** The TradFi menu's rows in tag order; a tag with no listed pair gets none. */
export function tradFiRows(pairs: FuturesPair[], tags: PairTag[] | null): TradFiRow[] {
  const rows = tags?.map((tag) => ({ ...tag, market: { tag: tag.code } })) ?? [ALL_TRADFI_ROW];
  return rows.flatMap((row) => {
    const firstPairPath = filterMarketPairs(pairs, row.market, '')[0]?.path;
    return firstPairPath ? [{ ...row, firstPairPath }] : [];
  });
}
